import type { ReactNode } from 'react';
import { AlertTriangle, CheckCircle2, CircleAlert, Inbox, Info } from 'lucide-react';
import { Button } from '@/components/ui/Button';

type Variant = 'error' | 'success' | 'info' | 'warning';

const variantClasses: Record<Variant, string> = {
  error: 'border-danger/30 bg-danger-subtle text-danger',
  success: 'border-success/30 bg-success-subtle text-success',
  info: 'border-info/30 bg-info-subtle text-info',
  warning: 'border-warning/30 bg-warning-subtle text-warning',
};

const variantIcons: Record<Variant, ReactNode> = {
  error: <CircleAlert size={16} className="mt-0.5 shrink-0" aria-hidden="true" />,
  success: <CheckCircle2 size={16} className="mt-0.5 shrink-0" aria-hidden="true" />,
  info: <Info size={16} className="mt-0.5 shrink-0" aria-hidden="true" />,
  warning: <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden="true" />,
};

export function Alert({ variant = 'info', children }: { variant?: Variant; children: ReactNode }) {
  return (
    <div className={`flex items-start gap-2 rounded-card border px-4 py-3 text-sm ${variantClasses[variant]}`} role="alert">
      {variantIcons[variant]}
      <span className="min-w-0 flex-1">{children}</span>
    </div>
  );
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className={`flex flex-col items-start gap-3 rounded-card border px-4 py-3 text-sm ${variantClasses.error}`} role="alert">
      <div className="flex items-start gap-2">
        {variantIcons.error}
        <span>{message}</span>
      </div>
      {onRetry && (
        <Button type="button" variant="ghost" size="sm" onClick={onRetry} className="-ml-3 text-danger hover:text-danger">
          Poskusi znova
        </Button>
      )}
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  /** Optional single secondary action (a `<Button variant="secondary">`). */
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-card border border-line px-6 py-14 text-center">
      <Inbox size={28} className="text-fg-muted" aria-hidden="true" />
      <p className="font-medium text-fg">{title}</p>
      {description && <p className="max-w-md text-sm text-fg-secondary">{description}</p>}
      {action && <div className="mt-1">{action}</div>}
    </div>
  );
}
